// Fold a rule's near-identical rows into groups.
//
// The migration writes one row per employee, so a single rule routinely carries
// 20+ rows that differ only in which employee id they name. Listed one per line
// they read as "1 employee only" over and over — a wall that hides the one row
// that actually decides everybody (the catch-all). Rows that are the same shape
// therefore collapse into a single line you can expand.

import type { PolicyRuleRow } from '../../api/policyRules';
import { predicateLabel } from './policyLanguage';

export interface RowGroup {
  key: string;
  rows: PolicyRuleRow[];
  /** Set only when the group holds more than one row. */
  label?: string;
}

// Two rows are worth listing separately; three of the same shape is a wall.
export const GROUP_MIN = 3;

const EMPLOYEE_KEYS = new Set(['employee_in', 'employee_not_in']);

/** Rows sharing this string are interchangeable at a glance. */
function signature(row: PolicyRuleRow, inForce: boolean): string {
  const keys = Object.keys(row.applies_to ?? {}).sort().join('+');
  return [
    inForce ? 'live' : 'ended',
    row.scope,
    row.auto_generated ? 'auto' : 'manual',
    row.effective_from ?? '',
    row.effective_to ?? '',
    keys,
  ].join('|');
}

function groupLabel(rows: PolicyRuleRow[]): string {
  const keys = Object.keys(rows[0].applies_to ?? {});
  const n = rows.length;
  if (keys.length === 1 && EMPLOYEE_KEYS.has(keys[0])) {
    return `${n} rules for individual employees`;
  }
  if (keys.length === 0) return `${n} rules covering everyone`;
  return `${n} similar rules — ${predicateLabel(keys[0])}…`;
}

/**
 * Group consecutive-by-shape rows, preserving the order they arrive in.
 * A group of fewer than GROUP_MIN rows stays as individual entries, so a rule
 * with two exceptions still shows both.
 */
export function buildRowGroups(
  rows: PolicyRuleRow[],
  isInForce: (row: PolicyRuleRow) => boolean,
): RowGroup[] {
  const bySignature = new Map<string, PolicyRuleRow[]>();
  const order: string[] = [];

  rows.forEach((row) => {
    const key = signature(row, isInForce(row));
    if (!bySignature.has(key)) {
      bySignature.set(key, []);
      order.push(key);
    }
    bySignature.get(key)!.push(row);
  });

  const out: RowGroup[] = [];
  order.forEach((key) => {
    const members = bySignature.get(key)!;
    if (members.length < GROUP_MIN) {
      // Keep them separate — each row keeps its own id as the React key.
      members.forEach((row) => out.push({ key: row.id, rows: [row] }));
      return;
    }
    out.push({ key, rows: members, label: groupLabel(members) });
  });

  return out;
}
